/**
 * Slim page fragment for lazy-loading region blocks.
 * Requests __typename, sys.id, and jumpLink (when available) for mainRegionCollection
 * items so the initial page query stays under Contentful's 8KB limit. Each block then
 * fetches its full data via CONTENTFUL_METADATA hooks in Component (useXById).
 *
 * jumpLink (KL-1058) is included for destination modules so HTML ids can use the pretty
 * hash before the full entry fetch completes.
 *
 * Lives in king-living so it survives `yarn contentful generate` (which overwrites pwa-contentful).
 * Keep union members in sync with pwa-contentful page.fragment.js.
 */

import gql from 'graphql-tag';

import {BasePageFragment} from '@amblique/contentful/app/contentful/fragments/page/base-page.fragment';
import {BaseSeoFragment} from '@amblique/contentful/app/contentful/fragments/seo/base-seo.fragment';

export const PageFragmentSlim = gql`
  ${BasePageFragment}
  ${BaseSeoFragment}

  fragment PageFragmentSlim on Page {
    ...BasePageFragment

    mainRegionCollection(limit: 25) {
      items {
        __typename
        ... on Accordion {
          sys {
            id
          }
        }
        ... on AccordionPanel {
          sys {
            id
          }
          jumpLink
        }
        ... on Blog {
          sys {
            id
          }
        }
        ... on BlogGrid {
          sys {
            id
          }
          jumpLink
        }
        ... on BlogListing {
          sys {
            id
          }
        }
        ... on BlogRelatedArticles {
          sys {
            id
          }
        }
        ... on Breadcrumb {
          sys {
            id
          }
        }
        ... on Card {
          sys {
            id
          }
          jumpLink
        }
        ... on CategoryCardGrid {
          sys {
            id
          }
          jumpLink
        }
        ... on Carousel {
          sys {
            id
          }
        }
        ... on ProductTiles {
          sys {
            id
          }
          jumpLink
        }
        ... on Container {
          sys {
            id
          }
        }
        ... on DescriptionBlock {
          sys {
            id
          }
          jumpLink
        }
        ... on EmbedPanel {
          sys {
            id
          }
          jumpLink
        }
        ... on FeatureList {
          sys {
            id
          }
        }
        ... on FeaturedInWithLogosCf052 {
          sys {
            id
          }
          jumpLink
        }
        ... on HeroBanner {
          sys {
            id
          }
          jumpLink
        }
        ... on IconSet {
          sys {
            id
          }
          jumpLink
        }
        ... on Image {
          sys {
            id
          }
        }
        ... on KingDifferenceMediaCf013 {
          sys {
            id
          }
          jumpLink
        }
        ... on Link {
          sys {
            id
          }
        }
        ... on LoopingTicker {
          sys {
            id
          }
        }
        ... on MediaCarousel {
          sys {
            id
          }
          jumpLink
        }
        ... on MediaContentSection {
          sys {
            id
          }
        }
        ... on MediaWithScrollableText {
          sys {
            id
          }
          jumpLink
        }
        ... on Reviews {
          sys {
            id
          }
          jumpLink
        }
        ... on RichTextBlock {
          sys {
            id
          }
          jumpLink
        }
        ... on SectionPanel {
          sys {
            id
          }
          jumpLink
        }
        ... on ShopTheLook {
          sys {
            id
          }
          jumpLink
        }
        ... on ShowroomDetails {
          sys {
            id
          }
          jumpLink
        }
        ... on SplitHeading {
          sys {
            id
          }
          jumpLink
        }
        ... on SplitPanelDoubleImageBlock {
          sys {
            id
          }
          jumpLink
        }
        ... on SplitText {
          sys {
            id
          }
        }
        ... on TeaserTileBlock {
          sys {
            id
          }
          jumpLink
        }
        ... on Video {
          sys {
            id
          }
        }
      }
    }
    seo {
      ...BaseSeoFragment
    }
  }
`;
